/**
 * Iter-412 — Rubric-driven assignment grader.
 *
 * Reads the assignment's `rubric` array (each entry: {id, name, max, guidance?})
 * and renders one row per criterion with a numeric input; the total is
 * computed live and stays capped at the assignment's `max_marks`. When the
 * assignment has no rubric we fall back to a single "Total marks" input.
 *
 * POST /api/school/portal/teacher/assignments/{aid}/submissions/{sid}/grade
 * with body { marks, feedback, marks_published, return_for_correction }
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Loader2, Save, Undo2, ClipboardCheck } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const _token = () => {
  try { const p = sessionStorage.getItem('school_preview_token'); if (p) return p; }
  catch { /* ignore */ }
  return getToken();
};

const AssignmentGraderDialog = ({ assignment, submission, open, onOpenChange, onGraded }) => {
  const qc = useQueryClient();
  const rubric = assignment?.rubric || [];
  const maxMarks = assignment?.max_marks || 100;

  // Per-criterion scores keyed by criterion id (or index if id missing)
  const [scores, setScores] = useState({});
  const [totalMarks, setTotalMarks] = useState(submission?.marks || 0);
  const [feedback, setFeedback] = useState(submission?.feedback || '');
  const [marksPublished, setMarksPublished] = useState(true);
  const [returnFlag, setReturnFlag] = useState(false);

  useEffect(() => {
    if (open) {
      const init = {};
      rubric.forEach((c, i) => {
        const key = c.id || `c-${i}`;
        init[key] = c.default ?? 0;
      });
      setScores(init);
      setTotalMarks(submission?.marks || 0);
      setFeedback(submission?.feedback || '');
      setMarksPublished(true);
      setReturnFlag(false);
    }
  }, [open, submission, rubric]);

  // Live total when rubric is in use
  const rubricTotal = useMemo(() => {
    if (rubric.length === 0) return null;
    return Math.min(maxMarks,
      rubric.reduce((sum, c, i) => sum + (Number(scores[c.id || `c-${i}`]) || 0), 0));
  }, [scores, rubric, maxMarks]);

  const effectiveTotal = rubricTotal ?? totalMarks;

  const grade = useMutation({
    mutationFn: () => fetch(
      `${API_URL}/api/school/portal/teacher/assignments/${assignment.id}/submissions/${submission.id}/grade`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${_token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          marks: effectiveTotal,
          feedback,
          marks_published: !!marksPublished,
          return_for_correction: !!returnFlag,
        }),
      },
    ).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(new Error(e.detail)))),
    onSuccess: () => {
      toast.success(returnFlag ? 'Returned for correction' : 'Grade saved');
      qc.invalidateQueries();
      onGraded?.();
      onOpenChange(false);
    },
    onError: (e) => toast.error(e.message),
  });

  if (!assignment || !submission) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="assignment-grader-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-indigo-500" />
            Grade — {submission.student_name || 'submission'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-slate-500 flex flex-wrap gap-3">
            <span>Assignment: <b>{assignment.title}</b></span>
            <span>Max: {maxMarks} marks</span>
            {submission.admission_number && <span>{submission.admission_number}</span>}
          </div>

          {rubric.length > 0 ? (
            <div className="space-y-2">
              <Label className="text-xs">Rubric</Label>
              {rubric.map((c, i) => {
                const key = c.id || `c-${i}`;
                const cmax = c.max ?? c.points ?? Math.round(maxMarks / rubric.length);
                return (
                  <div key={key} className="border rounded p-2 bg-slate-50"
                       data-testid={`rubric-row-${key}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">{c.name || `Criterion ${i + 1}`}</p>
                        {c.guidance && (
                          <p className="text-[11px] text-slate-500 line-clamp-2">{c.guidance}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Input
                          type="number"
                          min="0"
                          max={cmax}
                          step="0.5"
                          className="h-8 w-20 text-right"
                          value={scores[key] ?? 0}
                          onChange={(e) => setScores(s => ({
                            ...s, [key]: Math.min(cmax, Math.max(0, Number(e.target.value))),
                          }))}
                          data-testid={`rubric-score-${key}`}
                        />
                        <span className="text-xs text-slate-500">/ {cmax}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="flex justify-between items-center bg-indigo-50 rounded px-3 py-2">
                <span className="text-xs text-slate-600">Total (auto-summed)</span>
                <span className="text-lg font-semibold text-indigo-700" data-testid="rubric-total">
                  {rubricTotal}<span className="text-xs text-slate-500"> / {maxMarks}</span>
                </span>
              </div>
            </div>
          ) : (
            <div>
              <Label className="text-xs">Marks</Label>
              <Input
                type="number" min="0" max={maxMarks} step="0.5"
                value={totalMarks}
                onChange={(e) => setTotalMarks(Math.min(maxMarks, Math.max(0, Number(e.target.value))))}
                data-testid="total-marks-input"
              />
              <p className="text-[11px] text-slate-400 mt-0.5">
                No rubric attached — enter a total (max {maxMarks}).
              </p>
            </div>
          )}

          <div>
            <Label className="text-xs">Feedback (visible to student)</Label>
            <textarea
              className="w-full text-sm border rounded p-2 min-h-[70px]"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              data-testid="grader-feedback"
            />
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={marksPublished}
                     onChange={(e) => setMarksPublished(e.target.checked)}
                     data-testid="grader-publish" />
              Publish marks to student
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={returnFlag}
                     onChange={(e) => setReturnFlag(e.target.checked)}
                     data-testid="grader-return" />
              Return for correction
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => grade.mutate()} disabled={grade.isPending}
                  data-testid="grader-save">
            {grade.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" />
                              : (returnFlag ? <Undo2 className="h-4 w-4 mr-1" /> : <Save className="h-4 w-4 mr-1" />)}
            {returnFlag ? 'Return' : 'Save grade'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignmentGraderDialog;
